import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useBreadcrumbs } from './useBreadcrumbs';

export interface BreadcrumbBarProps {
  /** Şeridin sağ ucunda gösterilecek opsiyonel içerik. */
  rightSlot?: ReactNode;
}

/** İçerik alanının en üstünde gezinti şeridi — `location.pathname` + modül
 *  registry'sinden türetilen breadcrumb'lar. Yalnız "Anasayfa" varsa (kök) hiç
 *  render edilmez. */
export function BreadcrumbBar({ rightSlot }: BreadcrumbBarProps) {
  const crumbs = useBreadcrumbs();
  if (crumbs.length <= 1 && !rightSlot) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, sm: 2.5 }, minHeight: 28 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ flexGrow: 1, minWidth: 0, '& ol': { flexWrap: 'nowrap', overflow: 'hidden' } }}
      >
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          if (isLast || !crumb.to) {
            return (
              <Typography
                key={i}
                variant="body2"
                color={isLast ? 'text.primary' : 'text.secondary'}
                noWrap
                sx={{ fontWeight: isLast ? 600 : 400 }}
              >
                {crumb.label}
              </Typography>
            );
          }
          return (
            <Link
              key={i}
              component={RouterLink}
              to={crumb.to}
              variant="body2"
              color="text.secondary"
              underline="hover"
              noWrap
            >
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
      {rightSlot}
    </Box>
  );
}
